import { Button, Center, Menu, Paper, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconArchive, IconUpload } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

interface AppendicesEmptyStateProps {
  variant: 'no-documents' | 'all-archived';
  onFileDrop: (files: File[]) => void;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSharePointClick: () => void;
  onShowArchived?: () => void;
  isUploading: boolean;
}

export default function AppendicesEmptyState({
  variant,
  onFileDrop,
  onUpload,
  onSharePointClick,
  onShowArchived,
  isUploading,
}: AppendicesEmptyStateProps) {
  const isAllArchived = variant === 'all-archived';

  return (
    <Paper variant="content" p="xl">
      <Text size="displayXs" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="lg">
        Appendices
      </Text>
      <Center>
        <Stack align="center" gap="md" maw={500}>
          <Dropzone
            onDrop={onFileDrop}
            accept={ACCEPTED_FILE_TYPES}
            loading={isUploading}
            style={{
              border: '2px dashed var(--mantine-color-gray-4)',
              borderRadius: '12px',
              padding: '48px',
              width: '100%',
            }}
          >
            <Stack align="center" gap="sm">
              {isAllArchived ? (
                <IconArchive size={40} color="var(--mantine-color-gray-5)" />
              ) : (
                <IconUpload size={40} color="var(--mantine-color-gray-5)" />
              )}
              <Text size="lg" fw={FONT_WEIGHTS.medium} c="gray.7" ta="center">
                {isAllArchived ? 'No available documents' : 'No appendices yet'}
              </Text>
              <Text size="sm" c="gray.6" ta="center">
                {isAllArchived
                  ? 'All documents have been archived. Drag and drop PDF or DOCX files here, or click to browse.'
                  : 'Drag and drop PDF or DOCX files here, or click to browse'}
              </Text>
            </Stack>
          </Dropzone>
          <Menu shadow="md">
            <Menu.Target>
              <Button variant="primary" loading={isUploading}>
                Upload document
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item component="label" closeMenuOnClick={false} style={{ cursor: 'pointer' }}>
                Upload from your computer
                <input
                  type="file"
                  accept=".pdf,.docx"
                  multiple
                  style={{ display: 'none' }}
                  onChange={onUpload}
                />
              </Menu.Item>
              <Menu.Item onClick={onSharePointClick}>Upload from SharePoint</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {isAllArchived && onShowArchived && (
            <Button variant="subtle" onClick={onShowArchived}>
              Show archived documents
            </Button>
          )}
        </Stack>
      </Center>
    </Paper>
  );
}
